import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { getVMTDataForGHG } from '../../services/vmtDataService';
import { downloadChartAsPNG, formatFilename } from '../../utils/downloadUtils';
import {
  computeVMTComparisonMetrics,
  type VMTComparisonMetrics,
} from '../../utils/vmtSmartNarrative';
import './VMTComparisonChart.css';

export type { VMTComparisonMetrics };

Chart.register(...registerables);

/** Transportation page comparison view: align with inventory / published series (exclude provisional out-years). */
const VMT_COMPARISON_YEAR_MIN = 2005;
const VMT_COMPARISON_YEAR_MAX = 2023;

interface VMTComparisonChartProps {
  location: string;
  geofips: number | null;
  onDownloadPNGReady?: (handler: () => void) => void;
  onMetricsCalculated?: (metrics: VMTComparisonMetrics | null) => void;
}

interface VMTDataPoint {
  year: number;
  ldv: number;
  /** null when EMFAC HDV is not observed for this year (distinct from legitimate zero miles). */
  hdv: number | null;
  total: number;
}

export const VMTComparisonChart: React.FC<VMTComparisonChartProps> = ({
  location,
  geofips,
  onDownloadPNGReady,
  onMetricsCalculated,
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<VMTDataPoint[]>([]);

  const handleDownloadPNG = useCallback(() => {
    const filename = formatFilename(location, 'VMT_Comparison');
    downloadChartAsPNG(chartRef, filename);
  }, [location]);

  useEffect(() => {
    if (onDownloadPNGReady && !loading && !error && data.length > 0) {
      onDownloadPNGReady(handleDownloadPNG);
    }
  }, [onDownloadPNGReady, handleDownloadPNG, loading, error, data.length]);

  useEffect(() => {
    const fetchData = async () => {
      if (!location || !geofips) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        onMetricsCalculated?.(null);
        
        // Fetch LDV VMT from VMT chart data (uses correct methodology: Replica 2023, AADT backcast 2015-2022, EMFAC backcast 2005-2014)
        // NOTE: This uses the SAME calculated data as the VMT Chart (first chart) via getVMTDataForGHG()
        // which returns cached data from setVMTDataFromTransportation() called by the VMT Chart
        const vmtChartData = await getVMTDataForGHG(location, geofips || undefined);
        
        // Validate VMT data - check for missing or suspicious values
        if (vmtChartData.length === 0) {
          throw new Error('No LDV VMT data available');
        }
        
        // Check if 2022 is missing and backcast it from 2023 using AADT (same as GHG inventory)
        const vmt2022 = vmtChartData.find(d => d.year === 2022);
        const vmt2023 = vmtChartData.find(d => d.year === 2023);
        
        if ((!vmt2022 || vmt2022.vmt === 0) && vmt2023 && vmt2023.vmt > 0 && geofips) {
          try {
            // Fetch AADT data for 2022 (only need 2022 for backcasting from 2023)
            const { data: aadtData, error: aadtError } = await supabaseClient
              .from('aadt_filtered')
              .select('year, value')
              .eq('geofips', geofips)
              .eq('year', 2022)
              .single();
            
            // Try location_name fallback if geofips didn't work
            let aadtValue: number | null = null;
            if (!aadtError && aadtData && aadtData.value) {
              aadtValue = aadtData.value;
            } else {
              // Try location_name fallback
              const { data: aadtByName, error: aadtByNameError } = await supabaseClient
                .from('aadt_filtered')
                .select('year, value')
                .eq('location_name', location)
                .eq('year', 2022)
                .single();
              
              if (!aadtByNameError && aadtByName && aadtByName.value) {
                aadtValue = aadtByName.value;
              }
            }
            
            if (aadtValue !== null) {
              // Backcast: VMT_2022 = VMT_2023 × AADT_2022
              // AADT values are scaling factors: VMT_year = VMT_previous_year × AADT_year
              const backcastedVMT2022 = Math.round(vmt2023.vmt * aadtValue);
              // Add or update 2022 VMT in the data
              const vmt2022Index = vmtChartData.findIndex(d => d.year === 2022);
              if (vmt2022Index >= 0) {
                vmtChartData[vmt2022Index].vmt = backcastedVMT2022;
                vmtChartData[vmt2022Index].source = 'vmt_chart_backcasted';
                vmtChartData[vmt2022Index].method_note = 'VMT data from VMT Chart, 2022 backcasted from 2023 using AADT';
              } else {
                vmtChartData.push({
                  year: 2022,
                  vmt: backcastedVMT2022,
                  source: 'vmt_chart_backcasted',
                  method_note: 'VMT data backcasted from 2023 using AADT'
                });
                // Sort by year
                vmtChartData.sort((a, b) => a.year - b.year);
              }
            }
          } catch {
            // Backcasting 2022 VMT failed
          }
        }
        
        // Get HDV VMT from emfac2025_fleet
        // ALWAYS use geofips (not location_name) to avoid duplicates
        if (!geofips) {
          throw new Error('Geofips is required for HDV VMT data');
        }
        
        // Determine geography_type based on location (city vs county)
        // For cities, use 'City'; for counties, use 'County'
        // We can infer this from the geofips: city geofips are typically 6 digits, county geofips are 5 digits
        // But to be safe, we'll query for both and use the one that returns data
        let hdvData: any[] = [];
        let hdvError: any = null;
        
        // Try City first (most common for city locations)
        const { data: cityData, error: cityError } = await supabaseClient
          .from('emfac2025_fleet')
          .select('year, total_vmt, geofips, location_name, geography_type')
          .eq('geofips', geofips)
          .eq('geography_type', 'City')
          .eq('class', 'HDV')
          .gte('year', 2015)
          .lte('year', 2023)
          .order('year', { ascending: true });
        
        if (!cityError && cityData && cityData.length > 0) {
          hdvData = cityData;
        } else {
          // Try County as fallback
          const { data: countyData, error: countyError } = await supabaseClient
            .from('emfac2025_fleet')
            .select('year, total_vmt, geofips, location_name, geography_type')
            .eq('geofips', geofips)
            .eq('geography_type', 'County')
            .eq('class', 'HDV')
            .gte('year', 2015)
            .lte('year', 2023)
            .order('year', { ascending: true });
          
          if (!countyError && countyData && countyData.length > 0) {
            hdvData = countyData;
          } else {
            hdvError = countyError || cityError;
          }
        }
        
        if (hdvError) {
          console.error('Error fetching HDV data:', hdvError);
          throw hdvError;
        }
        
        // Process HDV data by year
        const hdvByYear = new Map<number, number>();
        
        // Sum HDV VMT for 2015-2023 from emfac2025_fleet
        (hdvData || []).forEach(row => {
          hdvByYear.set(row.year, (hdvByYear.get(row.year) || 0) + (row.total_vmt || 0));
        });
        
        // Apply data smoothing to detect and fix anomalies (similar to GHG inventory methodology)
        // If year-over-year change > 50%, use previous year's value instead
        const sortedYears = Array.from(hdvByYear.keys()).sort((a, b) => a - b);
        const smoothedHdvByYear = new Map<number, number>();
        
        sortedYears.forEach((year, index) => {
          const currentValue = hdvByYear.get(year) || 0;
          
          if (index === 0) {
            // First year: use as-is
            smoothedHdvByYear.set(year, currentValue);
          } else {
            const previousYear = sortedYears[index - 1];
            const previousValue = smoothedHdvByYear.get(previousYear) || 0;
            
            // Calculate year-over-year change percentage
            const changePercent = previousValue > 0 
              ? Math.abs(currentValue - previousValue) / previousValue 
              : 1;
            
            if (changePercent > 0.5) {
              // Anomaly detected: use previous year's value
              smoothedHdvByYear.set(year, previousValue);
            } else {
              // Normal change: use current value
              smoothedHdvByYear.set(year, currentValue);
            }
          }
        });
        
        // Replace original map with smoothed values
        hdvByYear.clear();
        smoothedHdvByYear.forEach((value, year) => {
          hdvByYear.set(year, value);
        });
        
        // Get 2015 HDV VMT as baseline for backcasting (use smoothed value)
        const hdv2015 = hdvByYear.get(2015) || 0;
        
        // Resolve county name for EMFAC backcasting ratios
        await supabaseLocationService.initialize().catch(() => {});
        const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
        const baseCountyName = resolvedCounty.replace(/\s+County$/i, '');
        const countyVariations = Array.from(new Set([
          `${baseCountyName} County`,
          baseCountyName,
          resolvedCounty
        ]));

        let emfacHdvRatios: { year: number; backcasting_ratio: number }[] | null = null;
        let emfacError: any = null;
        // Use EMFAC2025 for backcasting ratios
        for (const locName of countyVariations) {
          const { data, error } = await supabaseClient
            .from('emfac2025_county_filtered')
            .select('year, backcasting_ratio')
            .eq('location_name', locName)
            .eq('class', 'HDV')
            .in('year', [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014])
            .order('year', { ascending: true });
          if (!error && data && data.length > 0) {
            emfacHdvRatios = data as any;
            break;
          }
          emfacError = error;
        }
        // Backcast HDV for 2005-2014 using the ratios
        if (emfacHdvRatios && emfacHdvRatios.length > 0) {
          emfacHdvRatios.forEach(record => {
            if (record.backcasting_ratio) {
              const backcastedVmt = hdv2015 * record.backcasting_ratio;
              hdvByYear.set(record.year, backcastedVmt);
            }
          });
        }
        
        // Process data using yearMap
        const yearMap = new Map<number, { ldv?: number; hdv?: number }>();
        
        // Add LDV VMT data from the VMT chart (includes correct methodology: Replica 2023, AADT backcast 2015-2022, EMFAC backcast 2005-2014)
        vmtChartData.forEach(vmtRow => {
          yearMap.set(vmtRow.year, { ldv: vmtRow.vmt || 0 });
        });
        
        // Add HDV data (now includes backcasted data for 2005-2014)
        hdvByYear.forEach((vmt, year) => {
          if (!yearMap.has(year)) {
            yearMap.set(year, {});
          }
          const yearData = yearMap.get(year)!;
          yearData.hdv = vmt;
        });
        
        // Convert to array and sort by year
        let processedData: VMTDataPoint[] = Array.from(yearMap.entries())
          .map(([year, values]) => {
            const ldv = values.ldv || 0;
            const hdv = values.hdv !== undefined ? values.hdv : null;
            return {
              year,
              ldv,
              hdv,
              total: ldv + (hdv ?? 0),
            };
          })
          .sort((a, b) => a.year - b.year);
        
        // Validate and fix data anomalies (e.g., dramatic drops that are likely data errors)
        processedData = processedData.map((point, index) => {
          // Check for anomalies in LDV data
          if (point.ldv > 0 && index > 0 && index < processedData.length - 1) {
            const prevPoint = processedData[index - 1];
            const nextPoint = processedData[index + 1];
            
            // If current point is less than 20% of previous and next, it's likely an error
            const prevAvg = (prevPoint.ldv + nextPoint.ldv) / 2;
            if (point.ldv < prevAvg * 0.2 && prevPoint.ldv > 0 && nextPoint.ldv > 0) {
              // Interpolate from adjacent years
              const interpolatedLDV = (prevPoint.ldv + nextPoint.ldv) / 2;
              return {
                ...point,
                ldv: interpolatedLDV,
                total: interpolatedLDV + (point.hdv ?? 0)
              };
            }
          }
          return point;
        });
        
        processedData = processedData.filter(
          (d) => d.year >= VMT_COMPARISON_YEAR_MIN && d.year <= VMT_COMPARISON_YEAR_MAX
        );

        setData(processedData);
        if (onMetricsCalculated) {
          onMetricsCalculated(computeVMTComparisonMetrics(processedData));
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching VMT comparison data:', err);
        setError('Failed to load VMT comparison data');
        onMetricsCalculated?.(null);
        setLoading(false);
      }
    };

    fetchData();
  }, [location, geofips]);

  useEffect(() => {
    if (data.length === 0 || !chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Create new chart
    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: data.map(d => d.year.toString()),
        datasets: [
          {
            label: 'Light-Duty Vehicles (LDV)',
            data: data.map(d => d.ldv),
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: true,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: 'rgb(59, 130, 246)',
            pointBorderColor: 'rgb(59, 130, 246)'
          },
          {
            label: 'Heavy-Duty Vehicles (HDV)',
            data: data.map((d) => (d.hdv === null ? null : d.hdv)),
            borderColor: 'rgb(239, 68, 68)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            fill: true,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: 'rgb(239, 68, 68)',
            pointBorderColor: 'rgb(239, 68, 68)'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: standardLegend,
          title: standardTitle(`Light-Duty vs Heavy-Duty VMT - ${location}`),
          tooltip: {
            callbacks: {
              label: function(context) {
                const value = context.parsed?.y;
                return `${context.dataset.label}: ${(value ?? 0).toLocaleString()} miles`;
              }
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Year'
            }
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'VMT (miles)'
            },
            ticks: {
              callback: function(value) {
                return value != null && typeof value === 'number' ? value.toLocaleString() : '';
              }
            }
          }
        }
      }
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data, location]);

  if (loading) {
    return <div className="vmt-comparison-loading">Loading VMT comparison data...</div>;
  }

  if (error) {
    return <div className="vmt-comparison-error">{error}</div>;
  }

  if (data.length === 0) {
    return <div className="vmt-comparison-no-data">No VMT comparison data available</div>;
  }

  return (
    <div className="vmt-comparison-chart-container">
      <canvas ref={chartRef}></canvas>
    </div>
  );
};